import { css } from "@emotion/react"
import {
  Children,
  isValidElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
interface CustomSelectProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  className?: string
  id?: string
  placeholder?: string
  children: ReactNode
}

interface CustomSelectOptionProps {
  value: string
  children: React.ReactNode
  description?: string
}

interface CustomSelectOption {
  value: string
  label: string
  description?: string
}

function CustomSelect({
  value,
  onChange,
  onBlur,
  className,
  id,
  placeholder = "Choose an option",
  children,
  ...props
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)

  const options: CustomSelectOption[] = useMemo(() => {
    const extractedOptions: CustomSelectOption[] = []
    Children.forEach(children, (child) => {
      if (
        isValidElement<CustomSelectOptionProps>(child) &&
        child.type === CustomSelectOption
      ) {
        extractedOptions.push({
          value: child.props.value,
          label:
            typeof child.props.children === "string"
              ? child.props.children
              : child.props.value,
          description: child.props.description,
        })
      }
    })
    return extractedOptions
  }, [children])

  const selectedOption = options.find((option) => option.value === value)

  useEffect(() => {
    if (isOpen && selectedOption) {
      const selectedIndex = options.findIndex(
        (option) => option.value === value,
      )
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0)
    }
  }, [isOpen, selectedOption, value, options])

  const handleChange = (optionValue: string) => {
    onChange?.(optionValue)
    setIsOpen(false)
    buttonRef.current?.focus()
  }

  const handleTypeahead = (char: string) => {
    const selectedIndex = options.findIndex((option) => option.value === value)

    // find options that start with the typed character
    const matchingOptions = options
      .map((option, index) => ({ option, index }))
      .filter(({ option }) =>
        option.label.toLowerCase().startsWith(char.toLowerCase()),
      )

    if (matchingOptions.length === 0) return

    // cycle through matches after the current selection
    const currentMatchIndex = matchingOptions.findIndex(
      ({ index }) => index > selectedIndex,
    )
    const targetIndex =
      currentMatchIndex >= 0
        ? matchingOptions[currentMatchIndex].index
        : matchingOptions[0].index

    onChange(options[targetIndex].value)

    if (isOpen) {
      setHighlightedIndex(targetIndex)
    }
  }

  const scrollOptionIntoView = (index: number) => {
    requestAnimationFrame(() => {
      const option = listboxRef.current?.children[index] as HTMLElement
      option?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      })
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        if (isOpen) {
          const newIndex = (highlightedIndex + 1) % options.length
          setHighlightedIndex(newIndex)
          scrollOptionIntoView(newIndex)
        } else {
          setIsOpen(true)
        }
        break
      case "ArrowUp":
        e.preventDefault()
        if (isOpen) {
          const newIndex =
            (highlightedIndex - 1 + options.length) % options.length
          setHighlightedIndex(newIndex)
          scrollOptionIntoView(newIndex)
        } else {
          setIsOpen(true)
        }
        break
      case "Enter":
      case " ":
        e.preventDefault()
        if (isOpen) {
          handleChange(options[highlightedIndex].value)
        } else {
          setIsOpen(true)
        }
        break
      case "Escape":
        e.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
      case "Tab":
        if (isOpen) {
          setIsOpen(false)
        }
        break
      default:
        // handle typeahead for printable characters
        if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
          e.preventDefault()
          handleTypeahead(e.key)
        }
        break
    }
  }

  const handleClickOutside = (e: MouseEvent) => {
    if (
      !buttonRef.current?.contains(e.target as Node) &&
      !listboxRef.current?.contains(e.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  return (
    <div className={`p-r w-100% ${className}`}>
      <button
        {...props}
        ref={buttonRef}
        type="button"
        className="select ta-l"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
      >
        {selectedOption ? selectedOption.label : placeholder}
      </button>

      {isOpen && (
        <ul
          ref={listboxRef}
          role="listbox"
          aria-labelledby={id}
          className="p-a br-8p o-h ls-n p-0 m-0"
          css={styles.listbox}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onClick={() => handleChange(option.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleChange(option.value)
                }
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              tabIndex={-1}
              className="py-1 px-2 d-f fd-c g-2p"
              css={styles.option(index === highlightedIndex)}
            >
              <div
                className="fw-500"
                css={css`
                  &:only-child {
                    margin-block: 0.25rem;
                  }
                `}
              >
                {option.label}
              </div>
              {option.description && (
                <div className="fs-5 c-tint2">{option.description}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// component is only used for prop extraction, not rendering
function CustomSelectOption(_props: CustomSelectOptionProps) {
  return null
}

CustomSelect.Option = CustomSelectOption

const styles = {
  listbox: css`
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    z-index: 1000;
    background: var(--color-tint10);
    border: 1px solid var(--color-tint5);
    border-radius: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    min-width: min(calc(100vw - 3rem), 300px);
    overflow-y: auto;
  `,
  option: (isHighlighted: boolean) => css`
    cursor: pointer;
    background: ${isHighlighted ? "var(--color-tint7)" : "transparent"};
    border-bottom: 1px solid var(--color-tint5);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--color-tint7);
    }
  `,
}

export { CustomSelect }
